/**
 * Copyright (c) 2024-2026 Ronan LE MEILLAT
 * License: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";

/**
 * Minimal cart implementation stored in localStorage.  Several components
 * (navbar, product cards, cart page) access the same shared state via
 * context.
 */

/**
 * A single item stored in the shopping cart.
 */
type CartItem = {
  sku: string;
  title: string;
  price_cents: number;
  currency?: string;
  image_url?: string;
  qty: number;
};

/**
 * Shape of the cart context value provided by {@link CartProvider}.
 */
type CartContextType = {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  updateQuantity: (sku: string, qty: number) => void;
  removeItem: (sku: string) => void;
  clear: () => void;
  count: number;
  totalCents: number;
};

const STORAGE_KEY = "merchant_cart";

function readCart(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeCart(items: CartItem[]) {
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }
}

const CartContext = createContext<CartContextType | undefined>(undefined);

/**
 * Context provider that manages the global shopping cart state.
 * Cart contents are persisted in `localStorage` under the key `"merchant_cart"`,
 * so they survive page refreshes.
 *
 * Wrap your application (or the cart-aware subtree) with this provider:
 *
 * @example
 * ```tsx
 * <CartProvider>
 *   <App />
 * </CartProvider>
 * ```
 */
export const CartProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<CartItem[]>(() => readCart());

  useEffect(() => {
    writeCart(items);
  }, [items]);

  const addItem = useCallback((item: CartItem) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.sku === item.sku);
      if (existing) {
        return prev.map((i) =>
          i.sku === item.sku
            ? { ...i, qty: i.qty + item.qty, currency: item.currency ?? i.currency }
            : i,
        );
      }
      return [...prev, item];
    });
  }, []);

  const updateQuantity = useCallback((sku: string, qty: number) => {
    setItems((prev) => {
      if (qty <= 0) {
        return prev.filter((i) => i.sku !== sku);
      }
      return prev.map((i) => (i.sku === sku ? { ...i, qty } : i));
    });
  }, []);

  const removeItem = useCallback((sku: string) => {
    setItems((prev) => prev.filter((i) => i.sku !== sku));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((sum, i) => sum + i.qty, 0);
  const totalCents = items.reduce((sum, i) => sum + i.price_cents * i.qty, 0);

  const value: CartContextType = {
    items,
    addItem,
    updateQuantity,
    removeItem,
    clear,
    count,
    totalCents,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

/**
 * Returns the current cart context.
 * Must be called inside a {@link CartProvider}; throws otherwise.
 *
 * @returns The {@link CartContextType} value with items, totals, and mutation helpers.
 * @throws {Error} When called outside of a `<CartProvider>` tree.
 */
export function useCart(): CartContextType {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return ctx;
}
